/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : userUtils.ts
// 언어별 번역관리용 화일
//  - 로그인 화면 전용 번역 : login.json ({ ko, en, jp, zh, vn } 통합)
//  - 공통(mainFrame 등) 번역 : dd.json
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { Language, TranslationFunction, TranslationData } from './types';
////////////////////////////////////////////////////////////////////////////////////////////////////////////
// 사용자정의 모듈
//   Import JSON files
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import loginData from './login.json';  // 로그인 화면 전용 (ko/en/jp/zh/vn 통합)
import ddData from './dd.json';        // 공통(mainFrame+공통버튼/모달 등) - 정적 폴백(DB 조회 전/실패 시 사용)
import { getUserInfo } from '../utils/userUtils';

// ZA_DD 조회(apiDataDictionary) 응답 형식 - 로그인 시 mainFrame에서 받아와 setDDData()로 주입
//  - programs: pgmId -> gridId('agGrid1' 등, 그리드 아니면 'Single') -> { objId: text }
//    (ZA_DD 실제 PK가 PGM_ID+OBJ_ID+GRID_ID라서 gridId까지 유지해야 동일 PGM_ID 안의 그리드별 값이 안 섞임)
export interface DDLabelData {
  common: TranslationData;
  programs: Record<string, Record<string, TranslationData>>;
}
export type DDLabelResponse = Record<Language, DDLabelData>;

interface TranslationContextType {
  language: Language;
  getLanguage: (lang: Language) => void;
  ddKey: TranslationFunction;
  buildT: (programId?: string) => TranslationFunction; // programId 화면 전용 ddKey() 생성
  availableLanguages: Language[];
  setDDData: (dd: DDLabelResponse) => void; // 로그인 시 apiDataDictionary 응답을 주입
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// 로그인용(login.json) - 로그인 화면(인증 전)은 항상 이 정적 파일만 사용
const loginTranslations = loginData as unknown as Record<Language, TranslationData>;

// dd.json(정적) - DB(ZA_DD) 조회 응답이 아직 없을 때(로그인 직후 API 도착 전, 또는 조회 실패 시)의 폴백
const staticDDData = ddData as unknown as DDLabelResponse;

const SUPPORTED_LANGUAGES: Language[] = ['ko', 'en', 'jp', 'zh', 'vn'];

// 로그인 시 저장된 사용자 언어를 최우선으로 사용 - mainFrame이 마운트 후 별도 effect로 재적용할 필요가 없어져
// language 초기값(ko) -> 저장된 값으로 바뀌면서 language에 의존하는 effect(예: apiMainMenuTree)가 중복 호출되는 것을 방지
function getDefaultLanguage(): Language {
  const savedLanguage = getUserInfo()?.language;
  if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage as Language)) {
    return savedLanguage as Language;
  }
  // 브라우저/OS 언어 기반 결정 로직은 현재 비활성화 - 저장된 언어가 없으면 한글 기본
  const _lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'ko';
  //if (_lang.startsWith('ko')) return 'ko';
  //if (_lang.startsWith('en')) return 'en';
  //if (_lang.startsWith('jp')) return 'jp';
  //if (_lang.startsWith('zh')) return 'zh';
  //if (_lang.startsWith('vn')) return 'vn';
  return 'ko';
}

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, getLanguage] = useState<Language>(getDefaultLanguage());
  // DB(ZA_DD)에서 받아온 다국어 라벨 - 로그인 전에는 null이라 staticDDData(dd.json)로 폴백
  const [ddState, setDDData] = useState<DDLabelResponse | null>(null);

  // programId가 주어지면 programs[programId] 값을 common보다 우선 적용하는 ddKey()를 생성
  //  - 우선순위: programs[programId][gridId] → programs[programId]['Single'] → common → login.json → 없음
  //  - gridId는 ddKey() 호출 시점에 3번째 인자로 넘김(예: withDDHeader가 'agGrid1' 전달) - 생략하면 'Single' 기준
  //  - programId가 없으면 기존과 동일하게 common까지만 적용(하위호환)
  const buildT = useCallback((programId?: string): TranslationFunction => {
    return (key: string, params?: Record<string, string | number>, gridId?: string) => {
      // 2026-09-25: DB(ZA_DD) 응답이 와도 정적(dd.json) 값을 바탕으로 깔고 DB 값으로 덮어씀(DB 우선).
      //   DB에 COMMON(공통) 행이 아직 없거나 일부만 있어도 공통 버튼/라벨(btnCloseTab 등)이 사라지지 않게 하기 위함.
      const dbLang = ddState?.[language];
      const stLang = staticDDData[language];
      const dbBucket = (programId && dbLang?.programs?.[programId]) || {};
      const stBucket = (programId && stLang?.programs?.[programId]) || {};
      const pick = (grid: string): TranslationData => ({ ...(stBucket[grid] || {}), ...(dbBucket[grid] || {}) });
      const singleOverrides = pick('Single');
      const gridOverrides = gridId ? pick(gridId) : {};
      const merged: TranslationData = {
        ...loginTranslations[language],
        ...(stLang?.common ?? {}),
        ...(dbLang?.common ?? {}),
        ...singleOverrides,
        ...gridOverrides,
      };

      const keys = key.split('.');
      let value: string | TranslationData = merged;

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Fallback to key if translation not found
          //console.warn(`Translation key not found: ${key} for language: ${language}`);
          return '';
        }
      }

      if (typeof value !== 'string') {
        //console.warn(`Translation value is not a string: ${key} for language: ${language}`);
        return '';
      }

      // Replace parameters in the translation string
      if (params) {
        return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
          return params[paramKey]?.toString() || match;
        });
      }

      return value;
    };
  }, [language, ddState]);

  const ddKey = useMemo(() => buildT(), [buildT]);

  const contextValue: TranslationContextType = {
    language,
    getLanguage,
    ddKey,
    buildT,
    availableLanguages: ['ko', 'en', 'jp', 'vn', 'zh'],
    setDDData,
  };

  return React.createElement(
    TranslationContext.Provider,
    { value: contextValue },
    children ? children : null
  );
};

// 공통라벨 번역
//  - programId를 넘기면 해당 화면(programs[programId]) 라벨을 common보다 우선 적용한 ddKey()를 반환
//  - programId 없이 호출하면 기존과 동일(common까지만 적용)
export const useCommonTranslation = (programId?: string): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useCommonTranslation must be used within a TranslationProvider');
  }
  const scopedT = useMemo(() => context.buildT(programId), [context.buildT, programId]);
  return programId ? { ...context, ddKey: scopedT } : context;
};

// Export types
export type { Language, TranslationFunction, TranslationData };
